// Account & subscription page. Lets the signed-in user review their
// current subscription plan and jump to manage / change it. This is a
// focused landing surface for the "Account" item in the menu —
// audio/notification/biometric prefs live in /settings; this page is
// intentionally simple.

import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Mail,
  LogOut,
  Settings as SettingsIcon,
  ShieldCheck,
  Receipt,
} from "lucide-react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { AppMenu } from "../components/AppMenu";
import MembershipCard from "../components/MembershipCard";
import { ShareInviteButton } from "../components/ShareInviteButton";
import { useBilling } from "../contexts/BillingContext";
import { SiteFooter } from "../components/SiteFooter";

const PROVIDER_LABELS = {
  email: "Email + password",
  google: "Google",
};

const PROVIDER_FROM_USER = (user) => {
  if (!user) return "—";
  // Backend sets auth_provider on email users; Google users have no
  // explicit field (their `google_id` indicates it).
  if (user.auth_provider) return PROVIDER_LABELS[user.auth_provider] || user.auth_provider;
  if (user.google_id) return PROVIDER_LABELS.google;
  return "—";
};

const AccountPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // Billing status via the shared BillingContext (single fetch at
  // sign-in) — this page no longer makes its own /api/billing/status
  // request, which shaves ~200 ms off the visible load on iOS.
  const { billing, loading: billingLoading } = useBilling();

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="account-page">
      {/* Header */}
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              data-testid="account-back-btn"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-[#1B2349]" />
            </button>
            <div>
              <p className="text-[#718096] text-sm">Your space</p>
              <h1 className="font-outfit font-medium text-[#1B2349]">Account</h1>
            </div>
          </div>
          <AppMenu />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 pb-24">
        {/* Profile snapshot */}
        <section className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 mb-6 shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
          <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-3">Signed in as</p>
          <div className="flex items-center gap-3 mb-1">
            <Mail className="w-4 h-4 text-[#718096]" />
            <span className="font-outfit font-medium text-[#1B2349] text-lg break-all" data-testid="account-email">
              {user.email}
            </span>
          </div>
          {user.name && (
            <p className="text-[#4A5568] text-sm ml-7" data-testid="account-name">
              {user.name}
            </p>
          )}
          <p className="text-[#718096] text-xs mt-3 ml-7" data-testid="account-provider">
            Sign-in method: {PROVIDER_FROM_USER(user)}
          </p>
        </section>

        {/* Subscription / membership */}
        <section className="mb-6">
          <h2 className="font-outfit font-semibold text-xl text-[#1B2349] mb-3 px-1">
            Subscription
          </h2>
          {billingLoading ? (
            <div className="rounded-3xl border border-[#E5DCC9] bg-[#FBF8F1] p-8 text-center text-[#718096]" data-testid="account-billing-loading">
              Loading your membership…
            </div>
          ) : (
            <>
              <MembershipCard />
              {/* Quick stats row */}
              {billing && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                  <div className="rounded-2xl bg-white border border-slate-100 p-4">
                    <p className="text-[11px] uppercase tracking-wider text-[#718096] font-medium mb-1">Status</p>
                    <p className="text-[#1B2349] font-medium capitalize" data-testid="account-billing-status">
                      {billing.status || "—"}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white border border-slate-100 p-4">
                    <p className="text-[11px] uppercase tracking-wider text-[#718096] font-medium mb-1">Plan</p>
                    <p className="text-[#1B2349] font-medium" data-testid="account-billing-plan">
                      {billing.plan_id
                        ? billing.plan_id.charAt(0).toUpperCase() + billing.plan_id.slice(1)
                        : billing.status === "waived"
                        ? "Complimentary"
                        : "—"}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white border border-slate-100 p-4">
                    <p className="text-[11px] uppercase tracking-wider text-[#718096] font-medium mb-1">
                      {billing.status === "active" ? "Renews" : billing.status === "trialing" ? "Trial ends" : "Ends"}
                    </p>
                    <p className="text-[#1B2349] font-medium" data-testid="account-billing-expires">
                      {billing.expires_at
                        ? new Date(billing.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                        : "—"}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={`${window.location.origin}/pricing`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1B2349] text-white text-sm font-medium hover:bg-[#0F1838] transition-colors"
              data-testid="account-change-plan-btn"
            >
              Change or cancel plan
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href={`${window.location.origin}/pricing`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 text-[#1B2349] text-sm font-medium hover:bg-slate-50 transition-colors"
              data-testid="account-billing-history-btn"
            >
              <Receipt className="w-4 h-4" />
              Billing history
            </a>
          </div>
        </section>

        {/* Account management quick links */}
        <section className="mb-6">
          <h2 className="font-outfit font-semibold text-xl text-[#1B2349] mb-3 px-1">
            Account management
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => navigate("/settings")}
              className="text-left rounded-2xl bg-white border border-slate-100 p-5 hover:bg-slate-50 transition-colors flex items-start gap-3"
              data-testid="account-settings-link"
            >
              <SettingsIcon className="w-5 h-5 text-[#3090A8] mt-0.5" />
              <div>
                <p className="font-outfit font-medium text-[#1B2349]">App settings</p>
                <p className="text-sm text-[#718096] mt-0.5">Audio, narration, biometrics, notifications.</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => navigate("/settings#delete-account")}
              className="text-left rounded-2xl bg-white border border-slate-100 p-5 hover:bg-slate-50 transition-colors flex items-start gap-3"
              data-testid="account-privacy-link"
            >
              <ShieldCheck className="w-5 h-5 text-[#3090A8] mt-0.5" />
              <div>
                <p className="font-outfit font-medium text-[#1B2349]">Privacy & data</p>
                <p className="text-sm text-[#718096] mt-0.5">Download your data or delete your account.</p>
              </div>
            </button>
            {/* Contact — only surfaced for users with active access (active
                subscription or active free trial). Expired users and public
                visitors don't see this email. */}
            {billing?.entitled && (
              <a
                href="mailto:mscwellness@protonmail.com"
                className="text-left rounded-2xl bg-white border border-slate-100 p-5 hover:bg-slate-50 transition-colors flex items-start gap-3 sm:col-span-2"
                data-testid="account-contact-link"
              >
                <Mail className="w-5 h-5 text-[#3090A8] mt-0.5" />
                <div>
                  <p className="font-outfit font-medium text-[#1B2349]">Contact us</p>
                  <p className="text-sm text-[#718096] mt-0.5">mscwellness@protonmail.com — questions, feedback, or anything not working.</p>
                </div>
              </a>
            )}
          </div>
        </section>

        {/* Invite a friend + referral count */}
        <section className="mb-6">
          <h2 className="font-outfit font-semibold text-xl text-[#1B2349] mb-3 px-1">
            Share Bilateral Calm
          </h2>
          <ShareInviteButton variant="inline" />
        </section>

        {/* Sign out */}
        <section className="mt-10 flex justify-center">
          <Button
            variant="outline"
            onClick={logout}
            className="rounded-full border-slate-200 text-[#4A5568] hover:bg-slate-50 px-6"
            data-testid="account-signout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default AccountPage;
